import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { ConfigProvider } from './contexts/ConfigContext';
import { startSyncService } from './services/syncService';
import { initCacheMonitoring } from './utils/cacheCheck';

// Note: CSS files are loaded via <link> tags in index.html
// Do NOT import CSS files here as ES modules - they cause MIME type errors

// AGGRESSIVE startup cleanup - reduce storage immediately
// Deferred to unblock initial render
const performStartupCleanup = () => {
  try {
    // Remove backup and non-critical data
    localStorage.removeItem('ledger_sessions_backup');
    localStorage.removeItem('ledger_generated_materials');
    localStorage.removeItem('ai_training_feedback');
    localStorage.removeItem('feedback_reward_data');
    localStorage.removeItem('ledger_smart_cache');
    localStorage.removeItem('luca_feedback_training');

    // FORCE prune sessions if too large (> 1MB)
    const sessionsRaw = localStorage.getItem('ledger_sessions');
    if (sessionsRaw && sessionsRaw.length > 1000000) { // > 1MB
      console.log('[Startup] Sessions too large, pruning...');
      try {
        const sessions = JSON.parse(sessionsRaw);
        if (Array.isArray(sessions)) {
          // Keep only 5 most recent sessions with 10 messages each
          const pruned = sessions
            .sort((a: any, b: any) => (b.lastModified || 0) - (a.lastModified || 0))
            .slice(0, 5)
            .map((s: any) => ({
              ...s,
              messages: (s.messages || []).slice(-10).map((m: any) => ({
                ...m,
                content: m.content?.length > 10000 ? m.content.substring(0, 10000) + '...' : m.content,
                thought: undefined, // Remove thought process to save space
                attachments: [] // Remove attachments to save space
              })),
              generatedMaterials: []
            }));
          localStorage.setItem('ledger_sessions', JSON.stringify(pruned));
          console.log('[Startup] Pruned to', pruned.length, 'sessions');
        }
      } catch (e) {
        // If parsing fails, clear sessions entirely
        localStorage.removeItem('ledger_sessions');
        console.log('[Startup] Cleared corrupted sessions');
      }
    }
  } catch (e) {
    // Ignore errors
  }
};

// Defer cleanup to idle time
if ('requestIdleCallback' in window) {
  (window as any).requestIdleCallback(performStartupCleanup);
} else {
  setTimeout(performStartupCleanup, 2000);
}

// Initialize cache freshness check (prevents stale content)
initCacheMonitoring().catch(console.error);

// Global error handler for uncaught errors
window.addEventListener('error', (event) => {
  console.error('Global error caught:', event.error);
  // Prevent white screen by catching errors
  event.preventDefault();
});

// Global handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  // Prevent white screen by catching promise rejections
  event.preventDefault();
});

// Service Worker registration is handled by usePWA hook in App.tsx
// Removed duplicate registration here to prevent multiple registrations

// Start offline sync service
if (navigator.onLine) {
  try {
    startSyncService();
  } catch (err) {
    console.error('Failed to start sync service:', err);
  }
}

// Handle online/offline events
window.addEventListener('online', () => {
  console.log('[App] Back online');
  try {
    startSyncService();
  } catch (err) {
    console.error('Failed to start sync service:', err);
  }
});

window.addEventListener('offline', () => {
  console.log('[App] Gone offline');
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <ConfigProvider>
        <App />
      </ConfigProvider>
    </ErrorBoundary>
  </React.StrictMode>
);